import useSWR, {SWRConfiguration} from 'swr';
import useSWRMutation from 'swr/mutation';
import letrusAPI from 'APIs/letrusAPI/axiosInstance';
import {DjangoError, PaginatedRequest} from 'utils/types/store';
import {
  AiTutorAttemptParams,
  FinishQuestionnaireStepErrorResponse,
  FinishQuestionnaireStepSuccessResponse,
  QuestionAnswer,
  QuestionAttempt,
  QuestionParams,
  Questionnaire,
} from './types';
import {cachedQuestionAttempts} from './questionnaire.caches';

export function fetchQuestionnaireRequest(
  questionnaireId?: string,
  config?: SWRConfiguration<Questionnaire>,
) {
  return useSWR<Questionnaire>(
    `/api/v1/questionnaires/${questionnaireId}/`,
    (url) =>
      letrusAPI(url)
        .then((response) => response.data)
        .catch((error) => {
          throw error;
        }),
    {shouldRetryOnError: false, revalidateOnFocus: false, ...config},
  );
}

export function fetchQuestionAttemptRequest(
  params: QuestionParams,
  config?: SWRConfiguration<PaginatedRequest<QuestionAttempt[]>>,
) {
  return useSWR<PaginatedRequest<QuestionAttempt[]>>(
    [`/api/v1/question_attempts/`, params],
    ([url, queryParams]) =>
      letrusAPI(url, {method: 'get', params: queryParams})
        .then((response) => response.data)
        .catch((error) => {
          throw error;
        }),
    {shouldRetryOnError: false, revalidateOnFocus: false, ...config},
  );
}

export function updateQuestionAttemptRequest() {
  return useSWRMutation<QuestionAttempt, Error, string, QuestionAnswer>(
    `/api/v1/question_attempts/`,
    async (url, {arg}) => {
      const {responses, answer, ...body} = arg;

      try {
        const response = await letrusAPI(url, {method: 'post', data: arg});

        return response.data;
      } catch (error: any) {
        if (error?.message.includes('Network Error')) {
          const cachedData = await cachedQuestionAttempts.update(
            body,
            responses,
            answer,
          );
          return cachedData;
        }
        throw error;
      }
    },
    {throwOnError: false},
  );
}

export function sendAiTutorAttemptRequest() {
  return useSWRMutation<null, Error, string, AiTutorAttemptParams>(
    `/api/v1/question_attempts/ai_tutor_register/`,
    async (url, {arg}) =>
      letrusAPI(url, {method: 'post', data: arg})
        .then((response) => response.data)
        .catch((error) => {
          throw error;
        }),
    {throwOnError: false},
  );
}

export function finishQuestionnaireStepRequest(lpuiId?: string) {
  const mutationKey = lpuiId
    ? `/api/v1/new_learning_path_user_instances/${lpuiId}/finish_questionnaire_step/`
    : null;

  return useSWRMutation<
    FinishQuestionnaireStepSuccessResponse,
    DjangoError & {
      response?: {data?: FinishQuestionnaireStepErrorResponse};
    },
    string | null
  >(
    mutationKey,
    async (url) => {
      const response = await letrusAPI(url, {method: 'post'});
      return response.data;
    },
    {throwOnError: false},
  );
}
